import { useContext } from 'react'

import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import { Button, Flex, Header, spacing, Stack } from '@cash-design-system/react'
import { FormBlockerElementCallToActionElement } from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import logger from '@cash-web/shared/util-logger'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { NestedCard } from '@cash-web/shared-feature-common'
import { useTextTokenizer } from '@cash-web/shared-feature-use-text-tokenizer'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

const Wrapper = styled(NestedCard)`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.l};
  margin: ${({ theme: { spacing } }) => `${spacing.empty} ${spacing.xs}`};
`

const MaxWidthImage = styled.img`
  max-width: 100%;
`

const ActionHeader = styled(Header)`
  margin: ${({ theme: { spacing } }) => `${spacing.empty} ${spacing.xxs}`};
`

const ActionSubtitle = styled(Header)`
  margin: ${({ theme: { spacing } }) => `${spacing.empty} ${spacing.xxs}`};
  color: ${props => props.theme.colors.tertiaryLabel};
`

export const ActionButton = styled(Button)`
  margin: ${({ theme: { spacing } }) => `${spacing.l} ${spacing.xxs} ${spacing.xs} ${spacing.xxs}`};
`

// NOTE: The `accent_color` property is purposefully ignored: https://cash.slack.com/archives/C01M5JHQBCM/p1712264532738299
export const CallToActionElement = (props: FormBlockerElementCallToActionElement) => {
  const { setError } = useContext(ErrorContext)
  const { t } = useTranslation()
  const { darkMode } = useTheme()

  const flow = useFlowContext()

  const textTokenizer = useTextTokenizer({ textLink: { target: '_blank' } })

  const handleClick = () => {
    if (props?.button_action) {
      flow.executeBlockerAction(props?.button_action)
    } else {
      setError(t('This action is not currently supported', { context: 'Error text.' }))
      logger.error(new Error('Missing blocker action in CallToAction element'))
    }
  }

  // NOTE: The image_width and image_height properties are ignored. They are also ignored in iOS.
  const imageUrl = darkMode ? props.image?.dark_url : props.image?.light_url

  const tokenizedTitle = textTokenizer(props.title)
  const tokenizedSubtitle = textTokenizer(props.subtitle)

  return (
    <Wrapper>
      <Stack space={spacing.l}>
        {imageUrl && (
          <Flex justifyContent="center" alignItems="center">
            <MaxWidthImage src={imageUrl} alt="" />
          </Flex>
        )}
        <Stack space={spacing.s}>
          {props.title && (
            <ActionHeader data-testid="calltoaction-title" variant="titleBarPageTitle">
              {tokenizedTitle}
            </ActionHeader>
          )}
          {props.subtitle && (
            <ActionSubtitle data-testid="calltoaction-subtitle" variant="bodyDefault">
              {tokenizedSubtitle}
            </ActionSubtitle>
          )}
          {props.button_action && (
            <ActionButton variant="secondary" size="medium" loading={false} onClick={handleClick}>
              {props?.button_action?.text}
            </ActionButton>
          )}
        </Stack>
      </Stack>
    </Wrapper>
  )
}

export default CallToActionElement
